import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Search, Eye, Check, Truck, Package, X, Euro } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

export const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/admin/orders');
      setOrders(response.data.orders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}`, { status });
      toast.success('Statut mis à jour');
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status });
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-blue-100 text-blue-800 border-blue-200',
      ready: 'bg-purple-100 text-purple-800 border-purple-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200'
    };
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmée',
      ready: 'Prête',
      completed: 'Terminée',
      cancelled: 'Annulée'
    };
    return <Badge className={`${styles[status]} border`}>{labels[status]}</Badge>;
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#C9A96E] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header - Mobile Responsive */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-serif font-bold text-[#2C1810]">Gestion des Commandes</h1>
        <p className="text-sm md:text-base text-[#5D4037] mt-1">Suivez et gérez les commandes clients (paiement en magasin)</p>
      </div>

      {/* Filters - Mobile Responsive */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A67B5B]" size={20} />
              <Input
                placeholder="Rechercher par nom, numéro, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="confirmed">Confirmée</SelectItem>
                <SelectItem value="ready">Prête</SelectItem>
                <SelectItem value="completed">Terminée</SelectItem>
                <SelectItem value="cancelled">Annulée</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Desktop Table View */}
      <Card className="hidden lg:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F5F0EB]">
                <tr>
                  <th className="text-left p-4 text-[#2C1810] font-semibold">Commande</th>
                  <th className="text-left p-4 text-[#2C1810] font-semibold">Client</th>
                  <th className="text-left p-4 text-[#2C1810] font-semibold">Articles</th>
                  <th className="text-left p-4 text-[#2C1810] font-semibold">Total</th>
                  <th className="text-left p-4 text-[#2C1810] font-semibold">Statut</th>
                  <th className="text-right p-4 text-[#2C1810] font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length > 0 ? (
                  filteredOrders.map((order) => (
                    <tr key={order.id} className="border-b border-[#E8DDD4] hover:bg-[#F5F0EB]/50">
                      <td className="p-4">
                        <p className="font-mono font-semibold text-[#2C1810]">{order.orderNumber}</p>
                        <p className="text-xs text-[#5D4037]">
                          {new Date(order.createdAt).toLocaleDateString('fr-FR')}
                        </p>
                      </td>
                      <td className="p-4">
                        <p className="font-medium text-[#2C1810]">{order.customerName}</p>
                        <p className="text-sm text-[#5D4037]">{order.customerEmail}</p>
                        <p className="text-sm text-[#A67B5B]">{order.customerPhone}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-[#2C1810]">{order.items?.length || 0} article(s)</p>
                      </td>
                      <td className="p-4">
                        <p className="text-lg font-bold text-[#C9A96E]">{order.total?.toFixed(2)}€</p>
                      </td>
                      <td className="p-4">{getStatusBadge(order.status)}</td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setSelectedOrder(order)}
                            className="text-[#5D4037] hover:text-[#2C1810]"
                          >
                            <Eye size={18} />
                          </Button>
                          {order.status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => updateOrderStatus(order.id, 'confirmed')}
                              className="text-blue-600 hover:text-blue-700"
                              title="Confirmer"
                            >
                              <Check size={18} />
                            </Button>
                          )}
                          {order.status === 'confirmed' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => updateOrderStatus(order.id, 'ready')}
                              className="text-purple-600 hover:text-purple-700"
                              title="Marquer prête"
                            >
                              <Package size={18} />
                            </Button>
                          )}
                          {order.status === 'ready' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => updateOrderStatus(order.id, 'completed')}
                              className="text-green-600 hover:text-green-700"
                              title="Terminée"
                            >
                              <Euro size={18} />
                            </Button>
                          )}
                          {!['cancelled', 'completed'].includes(order.status) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => updateOrderStatus(order.id, 'cancelled')}
                              className="text-red-600 hover:text-red-700"
                              title="Annuler"
                            >
                              <X size={18} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-[#5D4037]">
                      Aucune commande trouvée
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {filteredOrders.length > 0 ? (
          filteredOrders.map((order) => (
            <Card key={order.id}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-mono font-bold text-[#2C1810]">{order.orderNumber}</p>
                    <p className="text-xs text-[#5D4037]">
                      {new Date(order.createdAt).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  {getStatusBadge(order.status)}
                </div>
                
                <div className="bg-[#F5F0EB] rounded-lg p-3 mb-3">
                  <p className="font-medium text-[#2C1810]">{order.customerName}</p>
                  <p className="text-sm text-[#5D4037]">{order.customerEmail}</p>
                  <p className="text-sm text-[#A67B5B]">{order.customerPhone}</p>
                </div>
                
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[#5D4037]">{order.items?.length || 0} article(s)</span>
                  <span className="text-xl font-bold text-[#C9A96E]">{order.total?.toFixed(2)}€</span>
                </div>
                
                <div className="flex flex-wrap gap-2 pt-3 border-t border-[#E8DDD4]">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedOrder(order)}
                    className="flex-1"
                  >
                    <Eye size={14} className="mr-1" /> Voir
                  </Button>
                  {order.status === 'pending' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateOrderStatus(order.id, 'confirmed')}
                      className="text-blue-600 border-blue-300"
                    >
                      <Check size={14} className="mr-1" /> Confirmer
                    </Button>
                  )}
                  {order.status === 'confirmed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateOrderStatus(order.id, 'ready')}
                      className="text-purple-600 border-purple-300"
                    >
                      <Package size={14} className="mr-1" /> Prête
                    </Button>
                  )}
                  {order.status === 'ready' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateOrderStatus(order.id, 'completed')}
                      className="text-green-600 border-green-300"
                    >
                      <Euro size={14} className="mr-1" /> Payée
                    </Button>
                  )}
                  {!['cancelled', 'completed'].includes(order.status) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateOrderStatus(order.id, 'cancelled')}
                      className="text-red-600 border-red-300"
                    >
                      <X size={14} />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="p-8 text-center text-[#5D4037]">
              <Package size={48} className="mx-auto text-[#D4C4B5] mb-4" />
              Aucune commande trouvée
            </CardContent>
          </Card>
        )}
      </div>

      {/* Order Detail Dialog - Mobile Optimized */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="w-[95vw] sm:w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif">Détails de la commande</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-2">
                <div>
                  <p className="font-mono font-bold text-[#2C1810]">{selectedOrder.orderNumber}</p>
                  <p className="text-sm text-[#5D4037]">
                    {new Date(selectedOrder.createdAt).toLocaleString('fr-FR')}
                  </p>
                </div>
                {getStatusBadge(selectedOrder.status)}
              </div>

              <div className="p-4 bg-[#F5F0EB] rounded-lg">
                <p className="font-semibold text-[#2C1810]">{selectedOrder.customerName}</p>
                <p className="text-sm text-[#5D4037]">{selectedOrder.customerEmail}</p>
                <p className="text-sm text-[#5D4037]">{selectedOrder.customerPhone}</p>
              </div>

              <div>
                <p className="font-semibold text-[#2C1810] mb-2">Articles commandés</p>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="flex justify-between p-3 bg-[#F5F0EB] rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#2C1810] truncate">{item.productName}</p>
                        <p className="text-sm text-[#5D4037]">Quantité: {item.quantity}</p>
                      </div>
                      <p className="font-semibold text-[#C9A96E] ml-2">
                        {(item.price * item.quantity).toFixed(2)}€
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-between items-center pt-4 border-t border-[#E8DDD4]">
                <span className="text-lg font-semibold text-[#2C1810]">Total</span>
                <span className="text-2xl font-bold text-[#C9A96E]">{selectedOrder.total?.toFixed(2)}€</span>
              </div>

              {selectedOrder.notes && (
                <div className="p-3 bg-[#F5F0EB] rounded-lg">
                  <p className="text-sm font-medium text-[#2C1810]">Notes :</p>
                  <p className="text-sm text-[#5D4037]">{selectedOrder.notes}</p>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Select
                  value={selectedOrder.status}
                  onValueChange={(status) => updateOrderStatus(selectedOrder.id, status)}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="confirmed">Confirmée</SelectItem>
                    <SelectItem value="ready">Prête</SelectItem>
                    <SelectItem value="completed">Terminée</SelectItem>
                    <SelectItem value="cancelled">Annulée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
